import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Clock, Play, Pause, Square, Calendar, History } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { safeFixed } from '../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SimplePunchClock({ employeeId, employeeName, isOwner = false, onViewPlanning, compact = false }) {
  const [punchStatus, setPunchStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (employeeId) {
      fetchPunchStatus();
    }
  }, [employeeId]);

  const fetchPunchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/employees/${employeeId}/punch-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPunchStatus(data);
      }
    } catch (error) {
      console.error('Error fetching punch status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePunch = async (punchType) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/employees/${employeeId}/punch`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ punch_type: punchType })
      });
      const data = await res.json();
      if (data.error) {
        toast.error(data.error);
      } else {
        const labels = { ARRIVAL: "Arrivée", PAUSE: "Pause", END: "Fin de journée" };
        toast.success(`${labels[punchType]} enregistré à ${data.punch.time}`);
        fetchPunchStatus();
      }
    } catch (error) {
      toast.error('Erreur de pointage');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const { currentState, totalWorkedHours, punches, canArrive, canPause, canEnd, employee } = punchStatus || {};

  const getStateInfo = () => {
    switch (currentState) {
      case 'WORKING':
        return { label: 'En service', color: 'bg-green-100 text-green-800', icon: '🟢' };
      case 'ON_BREAK':
        return { label: 'En pause', color: 'bg-amber-100 text-amber-800', icon: '☕' };
      case 'FINISHED':
        return { label: 'Journée terminée', color: 'bg-gray-100 text-gray-800', icon: '✓' };
      default:
        return { label: 'Non pointé', color: 'bg-orange-100 text-orange-800', icon: '⏸' };
    }
  };

  const stateInfo = getStateInfo();

  return (
    <div className={compact ? "space-y-4" : "space-y-6"} data-testid="simple-punch-clock">
      {/* Today's Date & Status */}
      <Card className={compact ? "bg-white border" : "bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20"}>
        {!compact && (
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2 text-xl">
                <Clock className="w-6 h-6 text-primary" />
                {format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })}
              </CardTitle>
              {onViewPlanning && (
                <Button variant="outline" onClick={onViewPlanning} size="sm">
                  <Calendar className="w-4 h-4 mr-2" />
                  Mon planning
                </Button>
              )}
            </div>
          </CardHeader>
        )}
        <CardContent className={compact ? "pt-4" : ""}>
          {/* Status & Hours */}
          <div className={`flex items-center justify-between ${compact ? 'mb-4' : 'mb-6'}`}>
            <Badge className={`${compact ? 'text-sm px-3 py-1' : 'text-lg px-4 py-2'} ${stateInfo.color}`}>
              <span className="mr-2">{stateInfo.icon}</span>
              {stateInfo.label}
            </Badge>
            <div className="text-right">
              <p className={`font-bold text-primary ${compact ? 'text-xl' : 'text-3xl'}`}>{safeFixed(totalWorkedHours, 1)}h</p>
              <p className="text-xs text-muted-foreground">
                sur {employee?.hoursPerDay || 8}h prévues
              </p>
            </div>
          </div>

          {/* 3 Action Buttons */}
          <div className="grid grid-cols-3 gap-3">
            {/* J'arrive */}
            <Button
              onClick={() => handlePunch('ARRIVAL')}
              disabled={!canArrive || actionLoading}
              className={`${compact ? 'h-16' : 'h-24'} flex flex-col items-center justify-center gap-1 ${compact ? 'text-sm' : 'text-lg'} font-semibold transition-all ${
                canArrive 
                  ? 'bg-green-600 hover:bg-green-700 hover:scale-105' 
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
              data-testid="punch-arrival-btn"
            >
              <Play className={compact ? "w-5 h-5" : "w-8 h-8"} />
              J'arrive
            </Button>

            {/* Pause */}
            <Button
              onClick={() => handlePunch('PAUSE')}
              disabled={!canPause || actionLoading}
              className={`${compact ? 'h-16' : 'h-24'} flex flex-col items-center justify-center gap-1 ${compact ? 'text-sm' : 'text-lg'} font-semibold transition-all ${
                canPause 
                  ? 'bg-amber-500 hover:bg-amber-600 hover:scale-105' 
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
              data-testid="punch-pause-btn"
            >
              <Pause className={compact ? "w-5 h-5" : "w-8 h-8"} />
              Pause
            </Button>

            {/* Fin de journée */}
            <Button
              onClick={() => handlePunch('END')}
              disabled={!canEnd || actionLoading}
              className={`${compact ? 'h-16' : 'h-24'} flex flex-col items-center justify-center gap-1 ${compact ? 'text-sm' : 'text-lg'} font-semibold transition-all ${
                canEnd 
                  ? 'bg-red-500 hover:bg-red-600 hover:scale-105' 
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
              data-testid="punch-end-btn"
            >
              <Square className={compact ? "w-5 h-5" : "w-8 h-8"} />
              Fin de journée
            </Button>
          </div>

          {/* Instructions - Only show in non-compact mode */}
          {!compact && (
            <p className="text-center text-sm text-muted-foreground mt-4">
              {currentState === 'NOT_STARTED' && "Cliquez sur 'J'arrive' pour commencer votre journée"}
              {currentState === 'WORKING' && "Vous êtes en service. Cliquez sur 'Pause' ou 'Fin de journée'"}
              {currentState === 'ON_BREAK' && "Cliquez sur 'J'arrive' pour reprendre le travail"}
              {currentState === 'FINISHED' && "Bonne soirée ! À demain."}
            </p>
          )}
        </CardContent>
      </Card>

      {/* History of Today's Punches - Hide in compact mode */}
      {!compact && punches && punches.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <History className="w-5 h-5" />
              Historique du jour
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {punches.map((punch, index) => (
                <div 
                  key={punch.id} 
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    punch.type === 'ARRIVAL' ? 'bg-green-50' :
                    punch.type === 'PAUSE' ? 'bg-amber-50' :
                    'bg-red-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      punch.type === 'ARRIVAL' ? 'bg-green-100 text-green-600' :
                      punch.type === 'PAUSE' ? 'bg-amber-100 text-amber-600' :
                      'bg-red-100 text-red-600'
                    }`}>
                      {punch.type === 'ARRIVAL' && <Play className="w-5 h-5" />}
                      {punch.type === 'PAUSE' && <Pause className="w-5 h-5" />}
                      {punch.type === 'END' && <Square className="w-5 h-5" />}
                    </div>
                    <div>
                      <p className="font-medium">{punch.typeLabel}</p>
                      <p className="text-xs text-muted-foreground">
                        {index === 0 ? 'Début de journée' : 
                         punch.type === 'PAUSE' ? 'Pause démarrée' :
                         punch.type === 'ARRIVAL' ? 'Reprise du travail' :
                         'Journée terminée'}
                      </p>
                    </div>
                  </div>
                  <p className="text-2xl font-bold font-mono">
                    {punch.time}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
